import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Image as ImageIcon, RotateCcw, Smartphone, Sparkles, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { api, type BotTemplate, type BotTemplatePart, type BotTemplatesResponse, type BotTemplateVariant } from '@/lib/api';
import { confirmAction, getWebApp, haptic } from '@/lib/telegram';
import { Switch } from '@/components/ui/switch';
import { Skeleton, SkeletonCard, SkeletonLine, SkeletonPage } from '@/components/ui/skeleton';
import { LoadError, NoBots, ScreenHeader, Section, useBackToHub, useSettingsBot } from '@/components/bot-settings/screen';

/**
 * Шаблоны оформления в мини-аппе кабинета: картинки 8 экранов с названием VPN, цвет
 * иконок и темы одним нажатием. Упрощённая копия раздела «Шаблоны» на сайте: без
 * телефона-превью (картинки листаются), а скачать архив картинок можно только на сайте,
 * потому что Telegram файлы из мини-аппа не сохраняет.
 *
 * `?from=onboarding` — сюда ведёт создание бота: вместо «Назад» есть «Пропустить».
 */
export const Route = createFileRoute('/bot/templates')({
  component: TemplatesScreen,
  validateSearch: (s: Record<string, unknown>): { bot?: string; from?: string } => ({
    ...(typeof s.bot === 'string' ? { bot: s.bot } : {}),
    ...(typeof s.from === 'string' ? { from: s.from } : {}),
  }),
});

const SITE_URL = 'https://hexpartners.pro/vpn/app/bot/templates';
const PART_ORDER: BotTemplatePart[] = ['banners', 'icons', 'miniapp', 'site'];
const SECTION_LABEL: Record<string, string> = {
  main: 'Главная',
  profile: 'Профиль',
  buy: 'Покупка',
  subs: 'Подписки',
  locations: 'Локации',
  promo: 'Промокод',
  info: 'О сервисе',
  support: 'Поддержка',
};
const MINIAPP_THEME_LABEL: Record<string, string> = {
  graphite: 'Графит',
  schema: 'Схема',
  cloud: 'Облако',
  classic: 'Классика',
};
const SITE_THEME_LABEL: Record<string, string> = { map: 'Карта', pixel: 'Пиксель', block: 'Блок' };
const SKIP_REASON: Record<string, string> = {
  media_disabled: 'хранилище картинок не подключено',
  color_not_ready: 'иконки этого цвета ещё не готовы',
};
const PART_ICON: Record<BotTemplatePart, typeof ImageIcon> = {
  banners: ImageIcon,
  icons: Sparkles,
  miniapp: Smartphone,
  site: Globe,
};

function partTitle(part: BotTemplatePart, t: BotTemplate, v: BotTemplateVariant): string {
  switch (part) {
    case 'banners':
      return 'Картинки 8 экранов';
    case 'icons':
      return v.icon_color ? `Иконки: цвет «${v.label}»` : 'Иконки: белые';
    case 'miniapp':
      return `Мини-апп: ${MINIAPP_THEME_LABEL[t.miniapp_theme] ?? t.miniapp_theme}`;
    case 'site':
      return `Витрина: ${SITE_THEME_LABEL[t.site_theme] ?? t.site_theme}`;
  }
}

function TemplatesScreen() {
  const { bot: botParam, from } = Route.useSearch();
  const { bot, isPending, isError, refetch } = useSettingsBot(botParam);
  useBackToHub();
  if (isPending) return <TemplatesSkeleton />;
  if (isError) return <LoadError onRetry={refetch} />;
  if (!bot) return <NoBots />;
  return <TemplatesBody key={bot.id} botId={bot.id} onboarding={from === 'onboarding'} />;
}

function TemplatesBody({ botId, onboarding }: { botId: string; onboarding: boolean }) {
  const q = useQuery({ queryKey: ['bot-templates', botId], queryFn: () => api.botTemplates(botId) });
  const { data: bots = [] } = useQuery({ queryKey: ['bots'], queryFn: () => api.bots(), staleTime: 60_000 });
  const listBot = bots.find((b) => b.id === botId);
  if (q.isPending) return <TemplatesSkeleton />;
  if (q.isError || !q.data) return <LoadError onRetry={() => void q.refetch()} />;
  return (
    <div className="space-y-5 px-3 pb-[calc(env(safe-area-inset-bottom)+110px)]">
      {listBot ? <ScreenHeader title="Шаблоны оформления" bot={listBot} /> : null}
      {q.data.templates.length === 0 ? (
        <p className="px-1 text-sm text-dim">Шаблонов пока нет. Они появятся здесь, как только мы их добавим.</p>
      ) : (
        <TemplatesForm botId={botId} data={q.data} onboarding={onboarding} />
      )}
    </div>
  );
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return v;
}

function TemplatesForm({
  botId,
  data,
  onboarding,
}: {
  botId: string;
  data: BotTemplatesResponse;
  onboarding: boolean;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [templateId, setTemplateId] = useState(data.last_application?.template ?? data.templates[0].id);
  const template = data.templates.find((t) => t.id === templateId) ?? data.templates[0];
  const [variantId, setVariantId] = useState(data.last_application?.variant ?? template.variants[0].id);
  const variant = template.variants.find((v) => v.id === variantId) ?? template.variants[0];

  const [name, setName] = useState(data.default_name);
  const cleanName = name.replace(/\s+/g, ' ').trim();
  const tooLong = cleanName.length > template.max_name_chars;
  const previewName = useDebounced(tooLong ? '' : cleanName, 400);
  const [parts, setParts] = useState<Set<BotTemplatePart>>(
    () => new Set(PART_ORDER.filter((p) => variant.parts[p]?.available)),
  );
  const [enablePremium, setEnablePremium] = useState(false);

  useEffect(() => {
    setParts((prev) => new Set([...prev].filter((p) => variant.parts[p]?.available)));
  }, [variant]);

  const previewQ = useQuery({
    queryKey: ['bot-template-preview', template.id, variant.id, previewName],
    queryFn: () => api.botTemplatePreview(template.id, variant.id, previewName),
    placeholderData: keepPreviousData,
    staleTime: Infinity,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['bot-templates', botId] });
    void qc.invalidateQueries({ queryKey: ['bot', botId] });
    void qc.invalidateQueries({ queryKey: ['bots'] });
  };

  const apply = useMutation({
    mutationFn: (body: { parts: BotTemplatePart[]; name: string }) =>
      api.applyBotTemplate(botId, {
        template: template.id,
        variant: variant.id,
        parts: body.parts,
        name: body.name,
        enable_premium_emoji: enablePremium,
      }),
    onSuccess: (r) => {
      haptic('success');
      const skipped = Object.entries(r.skipped);
      toast.success('Шаблон применён', {
        description: skipped.length
          ? `Не применилось: ${skipped.map(([p, why]) => `${partTitle(p as BotTemplatePart, template, variant).toLowerCase()} (${SKIP_REASON[why] ?? why})`).join(', ')}`
          : 'Откройте бота и посмотрите, как он выглядит теперь.',
      });
      setEnablePremium(false);
      refresh();
      if (onboarding) void navigate({ to: '/bot/settings', search: { bot: botId } });
    },
    onError: (e) => {
      haptic('error');
      toast.error(e instanceof Error && e.message ? e.message : 'Не удалось применить шаблон');
    },
  });

  const revert = useMutation({
    mutationFn: () => api.revertBotTemplate(botId),
    onSuccess: () => {
      haptic('success');
      toast.success('Вернули как было');
      refresh();
    },
    onError: () => toast.error('Не удалось вернуть. Попробуйте ещё раз.'),
  });

  const chosen = PART_ORDER.filter((p) => parts.has(p));
  const blocked = chosen.length === 0 || (parts.has('banners') && tooLong);

  // Главная кнопка Telegram «Применить». Замок от второго тапа: состояние мутации
  // доходит до обработчика только после рендера.
  const lockRef = useRef(false);
  const onApplyRef = useRef<() => void>(() => undefined);
  onApplyRef.current = () => {
    if (lockRef.current || blocked || revert.isPending) return;
    lockRef.current = true;
    void confirmAction(
      `Заменится: ${chosen.map((p) => partTitle(p, template, variant).toLowerCase()).join(', ')}. Тексты и цены останутся вашими.`,
      { title: `Применить «${template.title}»?`, okText: 'Применить', destructive: false },
    ).then((ok) => {
      if (ok) apply.mutate({ parts: chosen, name: cleanName });
      else lockRef.current = false;
    });
  };
  useEffect(() => {
    if (!apply.isPending) lockRef.current = false;
  }, [apply.isPending]);
  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.MainButton) return;
    const onMain = () => onApplyRef.current();
    tg.MainButton.setText('ПРИМЕНИТЬ К БОТУ');
    tg.MainButton.show();
    tg.MainButton.onClick(onMain);
    return () => {
      tg.MainButton.offClick(onMain);
      tg.MainButton.hideProgress();
      tg.MainButton.enable();
      tg.MainButton.hide();
    };
  }, []);
  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.MainButton) return;
    if (apply.isPending) tg.MainButton.showProgress();
    else tg.MainButton.hideProgress();
    if (blocked) tg.MainButton.disable?.();
    else tg.MainButton.enable?.();
  }, [apply.isPending, blocked]);

  const banners = previewQ.data?.banners;
  const last = data.last_application;

  return (
    <>
      {data.templates.length > 1 ? (
        <div className="-mx-3 flex snap-x gap-2 overflow-x-auto px-3 pb-1 [scrollbar-width:none]">
          {data.templates.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                haptic('selection');
                setTemplateId(t.id);
                setVariantId(t.variants[0].id);
              }}
              className={`shrink-0 snap-start rounded-full border px-4 py-2 text-sm ${t.id === template.id ? 'border-white/50 bg-white/10' : 'border-white/15'}`}
            >
              {t.title}
            </button>
          ))}
        </div>
      ) : null}

      <section className="space-y-2">
        <div className="-mx-3 flex snap-x snap-mandatory gap-2.5 overflow-x-auto overscroll-x-contain px-3 [scrollbar-width:none]">
          {data.sections.map((s) => (
            <figure key={s} className="w-[82%] shrink-0 snap-center">
              <div className="aspect-[3/1] overflow-hidden rounded-[14px] bg-black">
                {banners ? (
                  <img key={banners[s]} src={banners[s]} alt="" className="size-full object-cover" draggable={false} />
                ) : (
                  <Skeleton className="size-full rounded-none" />
                )}
              </div>
              <figcaption className="mt-1.5 px-1 text-[12px] text-dim">{SECTION_LABEL[s] ?? s}</figcaption>
            </figure>
          ))}
        </div>
        <p className="px-1 text-[12px] text-dim">{template.description}</p>
      </section>

      <Section title="Название на картинках">
        <input
          value={name}
          maxLength={64}
          onChange={(e) => setName(e.target.value)}
          placeholder="Без названия"
          aria-invalid={tooLong || undefined}
          className="w-full rounded-[13px] px-4 py-3 text-[16px] surface-inset focus:border-white/30 focus:outline-none"
        />
        <p className={`mt-2 text-[12px] ${tooLong ? 'text-danger' : 'text-dim'}`} aria-live="polite">
          {tooLong
            ? `Не поместится: сократите до ${template.max_name_chars} символов`
            : `Впишем в плашку над заголовком. Осталось ${template.max_name_chars - cleanName.length} симв.`}
        </p>
      </Section>

      {template.variants.length > 1 ? (
        <Section title="Цвет">
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Цвет">
            {template.variants.map((v) => (
              <button
                key={v.id}
                type="button"
                role="radio"
                aria-checked={v.id === variant.id}
                aria-label={v.label}
                onClick={() => {
                  haptic('selection');
                  setVariantId(v.id);
                }}
                className={`grid size-11 place-items-center rounded-full border ${v.id === variant.id ? 'border-white/60' : 'border-transparent'}`}
              >
                <span className="size-7 rounded-full" style={{ background: v.swatch }} />
              </button>
            ))}
          </div>
        </Section>
      ) : null}

      <Section
        title="Что применить"
        hint="Тексты, цены и кнопки останутся вашими. Передумаете, вернёте как было одной кнопкой."
      >
        <div className="divide-y divide-white/[0.06]">
          {PART_ORDER.map((p) => {
            const state = variant.parts[p];
            const Icon = PART_ICON[p];
            const unavailable = !state?.available;
            return (
              <label key={p} className="flex min-h-12 items-center gap-3 py-2.5 first:pt-0 last:pb-0">
                <Icon className="size-4 shrink-0 text-dim" aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className={`block text-[15px] ${unavailable ? 'text-dim' : ''}`}>
                    {partTitle(p, template, variant)}
                  </span>
                  {unavailable ? (
                    <span className="block text-[12px] text-dim">{SKIP_REASON[state?.reason ?? ''] ?? 'недоступно'}</span>
                  ) : null}
                </span>
                <Switch
                  checked={parts.has(p)}
                  disabled={unavailable}
                  aria-label={partTitle(p, template, variant)}
                  onCheckedChange={(on) => {
                    haptic('selection');
                    setParts((prev) => {
                      const next = new Set(prev);
                      if (on) next.add(p);
                      else next.delete(p);
                      return next;
                    });
                  }}
                />
              </label>
            );
          })}
        </div>
        {parts.has('icons') && variant.parts.icons?.reason === 'premium_off' ? (
          <div className="mt-3 space-y-2 rounded-[14px] bg-white/[0.04] p-3 text-[12.5px] text-dim">
            <p>
              Иконки рисуются премиум-эмодзи, а у бота они выключены. Включайте, если у владельца бота есть Telegram
              Premium или юзернейм с Fragment: иначе Telegram покажет обычные эмодзи.
            </p>
            <label className="flex items-center justify-between gap-3 text-[14px] text-foreground">
              Включить премиум-эмодзи
              <Switch checked={enablePremium} onCheckedChange={setEnablePremium} aria-label="Включить премиум-эмодзи" />
            </label>
          </div>
        ) : null}
      </Section>

      <div className="space-y-2">
        <button
          type="button"
          onClick={() => {
            haptic('light');
            const tg = getWebApp();
            if (tg?.openLink) tg.openLink(SITE_URL);
            else window.open(SITE_URL, '_blank', 'noopener');
          }}
          className="flex w-full items-center gap-3 rounded-[14px] border border-white/[0.12] bg-white/[0.07] p-3.5 text-left active:scale-[0.99]"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] font-medium">Скачать картинки на сайте</span>
            <span className="block text-[12px] text-dim">Telegram не сохраняет файлы из мини-аппа</span>
          </span>
          <ExternalLink className="size-4 shrink-0 text-dim" aria-hidden />
        </button>
        {onboarding ? (
          <button
            type="button"
            onClick={() => void navigate({ to: '/bot/settings', search: { bot: botId } })}
            className="w-full rounded-[14px] py-3 text-[15px] text-dim"
          >
            Пропустить, оформлю позже
          </button>
        ) : null}
        {last ? (
          <button
            type="button"
            disabled={revert.isPending || apply.isPending}
            onClick={async () => {
              const ok = await confirmAction('Вернём то, что стояло до шаблона. Ваши правки после шаблона не тронем.', {
                title: 'Вернуть как было?',
                okText: 'Вернуть',
                destructive: false,
              });
              if (ok) revert.mutate();
            }}
            className="flex w-full items-center justify-center gap-2 rounded-[14px] py-3 text-[14px] text-dim disabled:opacity-50"
          >
            <RotateCcw className="size-4" aria-hidden />
            Вернуть как было до «{data.templates.find((t) => t.id === last.template)?.title ?? last.template}»
          </button>
        ) : null}
      </div>
    </>
  );
}

function TemplatesSkeleton() {
  return (
    <SkeletonPage>
      <div className="space-y-5 px-3">
        <SkeletonLine className="w-48" />
        <Skeleton className="aspect-[3/1] w-[82%] rounded-[14px]" />
        <SkeletonCard className="h-28" />
        <SkeletonCard className="h-56" />
      </div>
    </SkeletonPage>
  );
}
